import { Box, Typography } from "@mui/material";
import DashboardNoFindingSvg from "~/components/icons/mondoo/DashboardNoFindingSvg";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";

interface PriorityFindingsCardEmptyStateFindingsProps {
  assetsTotalCount: number;
  activePoliciesCount: number | undefined;
  totalFindings: number;
}

export const PriorityFindingsCardEmptyStateFindings = ({
  assetsTotalCount,
  activePoliciesCount,
  totalFindings,
}: PriorityFindingsCardEmptyStateFindingsProps) => {
  const { isFeatureEnabled } = useFeatureFlags();

  const getNoFindingsMessage = (): string => {
    if (!assetsTotalCount) {
      return "Because this space contains no assets, there are no security findings.";
    }

    if (!activePoliciesCount) {
      return "Because there are no security policies enabled in this space, there are no security findings.";
    }

    if (activePoliciesCount > 0) {
      return `Mondoo scanned your inventory for ${totalFindings} known security issues and misconfigurations. It found none. Woohoo!`;
    }

    return "No security findings found";
  };

  const isMoveTheNeedleFeatureEnabled = isFeatureEnabled("Move the needle");

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "calc(100% - 24px)",
        gap: { xs: 2, sm: isMoveTheNeedleFeatureEnabled ? 1 : 3 },
        p: { xs: 2, sm: 3 },
        textAlign: "center",
      }}
    >
      {assetsTotalCount > 0 &&
        activePoliciesCount &&
        activePoliciesCount > 0 && (
          <Box
            sx={{
              marginTop: isMoveTheNeedleFeatureEnabled ? 0 : 5,
              "& svg": {
                width: 48,
                height: 48,
                color: "text.secondary",
              },
            }}
          >
            <DashboardNoFindingSvg />
          </Box>
        )}

      <Box
        sx={{
          maxWidth: {
            xs: "100%",
            sm: isMoveTheNeedleFeatureEnabled ? 420 : 400,
          },
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "700",
            fontSize: { xs: "1.125rem", sm: "1.5rem" },
            mb: { xs: 1, sm: 1.5 },
            lineHeight: 1.2,
          }}
        >
          No security findings found
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: "medium",
            color: "text.secondary",
            textAlign: "center",
            fontSize: { xs: "0.875rem", sm: "1rem" },
            px: { xs: 1, sm: 0 },
            lineHeight: { xs: 1.4, sm: 1.5 },
          }}
        >
          {getNoFindingsMessage()}
        </Typography>
      </Box>
    </Box>
  );
};
